import { ErrorTracking } from '../tracking/error-tracking.service';
import {
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import {
  pluckListItemFromRawStore,
  Query,
  RawStoreListState,
  Reference,
  RootModelEntity,
} from './helpers';
import { RootModel, RootModelLists } from './root.model';

import { ContactsGetResultApi } from './api-typings/contacts/contacts';
import { ContestsGetResultApi } from './api-typings/contests/contests';
import { ThreadsGetResultApi } from './api-typings/messages/messages_types';
import {
  BidsGetResultApi,
  MilestoneRequestsGetResultApi,
  MilestonesGetResultApi,
  ProjectsGetResultApi,
} from './api-typings/projects/projects';
import { UserApi, UsersGetResultApi } from './api-typings/users/users';

import { BidsUpdateActionRawPayload } from './bid/bid.model';
import {
  MessagesGetApiResponse,
  MessagesPostApiResponse,
  MessagesPostRawPayload,
} from './message/message.model';
import {
  NavigationProjectFeedApiResponse,
  NavigationUserOnPaytmMembershipPlan,
  NewsfeedApiResponse,
} from './navigation/navigation.model';
import {
  NotificationsPreferenceApiResponse,
  NotificationsPreferencePostRawPayload,
} from './notification-preferences/notification-preferences.model';
import {
  SearchContestResultAjax,
  SearchFreelancersResultAjax,
  SearchHistoryPostRawPayload,
  SearchHistoryResultAjax,
  SearchKeywordProjectsResultAjax,
  SearchMyContactsResultAjax,
  SearchMyProjectsResultAjax,
} from './search/search.model';
import {
  Thread,
  ThreadsPostApiResponse,
  ThreadsPostRawPayload,
  ThreadsUpdateActionRawPayload,
} from './thread/thread.model';

export interface Params {
  [key: string]: string | number | string[] | number[];
}

export interface BackendConfig {
  endpoint: string;
  isGaf?: boolean;
  params: Params;
}

type Request = ((key: string, query?: Query) => BackendConfig);
type RequestList = ((key: string[], query?: Query) => BackendConfig);

interface Backend {
  fetch?: RequestList;
  post?: Request;
}

export const backend: { [T in keyof RootModel]: Backend } = {
  giveGetEligibility: {
    fetch: () => ({
      endpoint: `users/0.1/referral_invitations/check`,
      params: { bonus_id: '16' },
    }),
  },
  messages: {
    fetch: threadIds => ({
      endpoint: `messages/0.1/messages`,
      params: { threads: threadIds },
    }),
    post: threadId => ({
      endpoint: `messages/0.1/threads/${threadId}/messages/`,
      params: { thread_id: threadId },
    }),
  },
  navigationNotifications: {
    fetch: () => ({
      endpoint: `newsfeed/0.1/newsfeed/notifications`,
      params: {},
    }),
  },
  navigationProjectFeed: {
    fetch: () => ({
      endpoint: `navigation/project-feed/pre-populated.php`,
      isGaf: true,
      params: {},
    }),
  },
  navigationProjectFeedFailingProjects: {
    fetch: () => ({
      endpoint: `navigation/project-feed/pre-populated-xp-projectcontest.php`,
      isGaf: true,
      params: { type: 'p' },
    }),
  },
  navigationProjectFeedFailingContests: {
    fetch: () => ({
      endpoint: `navigation/project-feed/pre-populated-xp-projectcontest.php`,
      isGaf: true,
      params: { type: 'c' },
    }),
  },
  navigationUserBalances: {
    fetch: () => ({
      endpoint: `users/0.1/self`,
      params: { balance_details: 1 },
    }),
  },
  navigationPendingFunds: {
    fetch: () => ({
      endpoint: `users/0.1/self`,
      params: { balance_details: 1 },
    }),
  },
  navigationUserInfo: {
    fetch: () => ({
      endpoint: `navigation/user-info.php`,
      isGaf: true,
      params: {},
    }),
  },
  navigationUserRecentProjectsAndContests: {
    fetch: () => ({
      endpoint: `navigation/recent-projects-and-contests.php`,
      isGaf: true,
      params: {},
    }),
  },
  navigationUserOnPaytmMembershipPlan: {
    fetch: () => ({
      endpoint: `memberships/isUserOnPaytmMembershipPlan.php`,
      isGaf: true,
      params: {},
    }),
  },
  notificationsUserPreferences: {
    fetch: () => ({
      endpoint: `notifications/preferences.php`,
      isGaf: true,
      params: {},
    }),
    post: () => ({
      endpoint: `notifications/preferences.php`,
      isGaf: true,
      params: {},
    }),
  },
  onlineOffline: {},
  searchContests: {
    fetch: queries => ({
      endpoint: `search/contests.php`,
      isGaf: true,
      params: { q: queries[0] },
    }),
  },
  searchHistory: {
    fetch: () => ({
      endpoint: `search/history.php`,
      isGaf: true,
      params: {},
    }),
    post: keyword => ({
      endpoint: `search/history.php`,
      isGaf: true,
      params: {},
    }),
  },
  searchKeywordProjects: {
    fetch: queries => ({
      endpoint: `search/keywordProjects.php`,
      isGaf: true,
      params: { q: queries[0] },
    }),
  },
  searchMyContacts: {
    fetch: () => ({
      endpoint: `search/myContacts.php`,
      isGaf: true,
      params: {},
    }),
  },
  searchMyProjectsBuyer: {
    fetch: () => ({
      endpoint: `search/myProjects.php`,
      isGaf: true,
      params: { buyer: 'true', active: 'true' },
    }),
  },
  searchFreelancers: {
    fetch: queries => ({
      endpoint: `search/topFreelancer.php`,
      isGaf: true,
      params: { q: queries[0] },
    }),
  },
  threads: {
    fetch: (_, query) => {
      const val = {
        endpoint: `messages/0.1/threads`,
        params: {
          unread_count: 'true',
          last_message: 'true',
          message_details: 'true',
          context_details: 'true',
          user_details: 'true',
          user_read_times: 'true',
        },
      };
      if (query && query.params) {
        val.params = { ...val.params, ...query.params };
      }
      return val;
    },
    post: () => ({
      endpoint: `messages/0.1/threads/`,
      params: {},
    }),
  },
  threadContests: {
    fetch: contestIds => ({
      endpoint: `contests/0.1/contests`,
      params: {
        user_details: 'true',
        user_avatar: 'true',
        contests: contestIds,
      },
    }),
  },
  threadProjects: {
    fetch: projectIds => ({
      endpoint: `projects/0.1/projects`,
      params: {
        user_details: 'true',
        user_avatar: 'true',
        projects: projectIds,
      },
    }),
  },
  threadUsers: {
    fetch: userIds => ({
      endpoint: `users/0.1/users`,
      params: {
        support_status_details: 'true',
        avatar: 'true',
        users: userIds,
      },
    }),
  },
  contacts: {
    fetch: () => ({
      endpoint: `contacts/0.1/contacts`,
      params: {},
    }),
  },
  threadBids: {
    fetch: projectIds => ({
      endpoint: `projects/0.1/bids`,
      params: { projects: projectIds },
    }),
  },
  threadMilestones: {
    fetch: projectIds => ({
      endpoint: `projects/0.1/milestones`,
      params: { projects: projectIds },
    }),
  },
  threadMilestoneRequests: {
    fetch: projectIds => ({
      endpoint: `projects/0.1/milestone_requests`,
      params: { projects: projectIds },
    }),
  },
};

export interface BackendFetchTypes {
  // ThreadSupportSessions: SupportSessionApi;
  contacts: ContactsGetResultApi;
  giveGetEligibility: boolean;
  messages: MessagesGetApiResponse;
  navigationNotifications: NewsfeedApiResponse;
  navigationPendingFunds: UserApi;
  navigationProjectFeed: NavigationProjectFeedApiResponse;
  navigationProjectFeedFailingContests: NavigationProjectFeedApiResponse;
  navigationProjectFeedFailingProjects: NavigationProjectFeedApiResponse;
  navigationUserBalances: UserApi;
  navigationUserInfo: any; // TODO
  navigationUserOnPaytmMembershipPlan: NavigationUserOnPaytmMembershipPlan;
  navigationUserRecentProjectsAndContests: any; // TODO
  notificationsUserPreferences: NotificationsPreferenceApiResponse;
  onlineOffline: never;
  searchContests: SearchContestResultAjax[];
  searchFreelancers: SearchFreelancersResultAjax[];
  searchHistory: SearchHistoryResultAjax[];
  searchKeywordProjects: SearchKeywordProjectsResultAjax[];
  searchMyContacts: SearchMyContactsResultAjax[];
  searchMyProjectsBuyer: SearchMyProjectsResultAjax[];
  threadBids: BidsGetResultApi;
  threadContests: ContestsGetResultApi;
  threadMilestoneRequests: MilestoneRequestsGetResultApi;
  threadMilestones: MilestonesGetResultApi;
  threadProjects: ProjectsGetResultApi;
  threadUsers: UsersGetResultApi;
  threads: ThreadsGetResultApi;
}

export type BackendPushPayloadTypes = {
  messages: MessagesPostRawPayload;
  searchHistory: any; // TODO
  threads: ThreadsPostRawPayload;
} & { [k: string]: never };

export type BackendPushReturnTypes = {
  messages: MessagesPostApiResponse;
  searchHistory: any; // TODO
  threads: ThreadsPostApiResponse;
} & { [k: string]: never };

export type BackendSetPayloadTypes = {
  notificationsUserPreferences: NotificationsPreferencePostRawPayload;
} & { [k: string]: never };

export type BackendSetReturnTypes = {
  notificationsUserPreferences: NotificationsPreferenceApiResponse;
} & { [k: string]: never };

export type BackendUpdatePayloadTypes = {
  threads: ThreadsUpdateActionRawPayload;
  threadBids: BidsUpdateActionRawPayload;
} & { [k: string]: never };

export type BackendUpdateReturnTypes = {} & { [k: string]: never };

export type BackendDeletePayloadTypes = {} & { [k: string]: never };

export type BackendDeleteReturnTypes = {} & { [k: string]: never };

export function pushPayload<T extends keyof RootModel>(
  reference: Reference<T>,
  object: RootModelEntity<T>,
  errorTracking: ErrorTracking,
): PushRequestPayload<T> | undefined {
  const type = reference.path[0];
  switch (type) {
    case 'threads': {
      const ref = reference;
      const thread = object as RootModelEntity<'threads'>;
      const rawRequest: ThreadsPostRawPayload = {
        context: thread.context ? thread.context.id : undefined,
        context_type: thread.context ? thread.context.type : undefined,
        type: thread.threadType,
        members: thread.members,
        message: (thread.message && thread.message.message) || '',
        source: 21,
      };
      return { type, ref, object: thread, rawRequest };
    }
    case 'messages': {
      const ref = reference;
      const message = object as RootModelEntity<'messages'>;
      const rawRequest: MessagesPostRawPayload = {
        message: message.message,
        client_message_id: message.clientMessageId
          ? String(message.clientMessageId)
          : String(Date.now()),
        source: message.messageSource,
      };
      return { type, ref, object: message, rawRequest };
    }
    case 'searchHistory': {
      const ref = reference;
      const data = object as RootModelEntity<'searchHistory'>;
      const rawRequest: SearchHistoryPostRawPayload = {
        keyword: data.keyword,
      };
      return { type, ref, object: data, rawRequest };
    }

    default:
      errorTracking.captureMessage(`Cannot push for ${type}.`, {
        extra: { reference, object },
      });
      return undefined;
  }
}

export function setPayload<T extends keyof RootModelLists>(
  reference: Reference<T>,
  id: string | number,
  object: RootModelEntity<T>,
  store: RawStoreListState,
  errorTracking: ErrorTracking,
): SetRequestPayload<T> | undefined {
  const type = reference.path[0];

  switch (type) {
    case 'notificationsUserPreferences': {
      const ref = reference;
      const originalObject = pluckListItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const userPreference = object as RootModelEntity<
        'notificationsUserPreferences'
      >;
      const rawRequest: NotificationsPreferencePostRawPayload = {
        settings: JSON.stringify([userPreference]),
      };
      return {
        type,
        ref,
        object: userPreference,
        rawRequest,
        originalObject,
      };
    }

    default:
      errorTracking.captureMessage(`Cannot push for ${type}.`, {
        extra: { reference, object },
      });
      return undefined;
  }
}

export function updatePayload<T extends keyof RootModelLists>(
  reference: Reference<T>,
  id: string | number,
  object: Partial<RootModelEntity<T>>,
  store: RawStoreListState,
  errorTracking: ErrorTracking,
):
  | {
      payload: UpdateRequestPayload<T>;
      endpoint: string;
      method: 'POST' | 'PUT';
    }
  | undefined {
  const type = reference.path[0];
  const captureMessageError = { extra: { reference, object } };
  let method: 'PUT' | 'POST' = 'PUT';

  switch (type) {
    case 'threads': {
      const ref = reference;
      const originalObject = pluckListItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const originalThread = originalObject as Thread;
      const thread = (object as any) as Partial<RootModelEntity<'threads'>>;
      let rawRequest: ThreadsUpdateActionRawPayload | undefined;
      let endpoint: string | undefined;

      if (thread.isRead === true) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [Number(id)],
          action: 'read',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.isMuted !== undefined) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [Number(id)],
          action: thread.isMuted ? 'mute' : 'unmute',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.folder) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }

        if (thread.folder === 'inbox') {
          rawRequest = { threads: [Number(id)], action: 'unarchive' };
        } else if (thread.folder === 'archived') {
          rawRequest = { threads: [Number(id)], action: 'archive' };
        } else {
          errorTracking.captureMessage(
            `Cannot push for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        endpoint = `messages/0.1/threads/`;
      }

      if (
        thread.members &&
        thread.members.length !== originalThread.members.length
      ) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        endpoint = `messages/0.1/threads/${originalThread.id}/members/`;
        if (thread.members.length > originalThread.members.length) {
          rawRequest = {
            thread_id: originalThread.id,
            action: 'add',
            members: thread.members.filter(
              m => !originalThread.members.includes(m),
            ),
          };
        } else if (thread.members.length < originalThread.members.length) {
          rawRequest = {
            thread_id: originalThread.id,
            action: 'remove',
            members: originalThread.members.filter(
              m => !(thread.members || []).includes(m),
            ),
          };
        }
      }

      if (thread.typing) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = { thread_id: originalThread.id };
        endpoint = `messages/0.1/threads/${originalThread.id}/typing/`;
        method = 'POST';
      }
      if (rawRequest === undefined || endpoint === undefined) {
        errorTracking.captureMessage(
          `Cound not update any fields of ${type}.`,
          captureMessageError,
        );
        return undefined;
      }

      return {
        payload: {
          type,
          ref,
          object: thread as any,
          rawRequest,
          originalObject,
        },
        endpoint,
        method,
      };
    }
    case 'threadBids': {
      const ref = reference;
      const originalObject = pluckListItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const bid = (object as any) as Partial<RootModelEntity<'threadBids'>>;
      if (bid.awardStatus === 'awarded') {
        const endpoint = `projects/0.1/bids/${id}`;
        const rawRequest: BidsUpdateActionRawPayload = {
          bid_id: Number(id),
          action: 'accept',
        };
        return {
          payload: {
            type,
            ref,
            object: bid as any,
            rawRequest,
            originalObject,
          },
          endpoint,
          method,
        };
      }
      return undefined;
    }
    default:
      errorTracking.captureMessage(
        `Cannot update for ${type}.`,
        captureMessageError,
      );
      return undefined;
  }
}

export function deletePayload<T extends keyof RootModelLists>(
  reference: Reference<T>,
  id: string | number,
  store: RawStoreListState,
  errorTracking: ErrorTracking,
):
  | {
      payload: DeleteRequestPayload<T>;
      endpoint: string;
      method: 'POST' | 'PUT' | 'DELETE';
    }
  | undefined {
  const type = reference.path[0];
  const captureMessageError = { extra: { reference } };

  switch (type) {
    default:
      errorTracking.captureMessage(
        `Cannot delete for ${type}.`,
        captureMessageError,
      );
      return undefined;
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/ko/webapp/src/app/@shared/datastore/backend.ts